import React, { useId, useState } from "react";
import { useIntl } from "react-intl";
import classNames from "classnames";
import { useWebchatPreviewMode } from "@/hook";
import { NumberInput, RangeSlider } from "@/component/input";

import { WebchatEmbeddedFormProps } from ".";
import { WebchatRadioGroup, WebchatRadioGroupOption } from "../component";


const BUTTON_ALIGN_OPTIONS: WebchatRadioGroupOption<"left" | "right">[] = [
    { value: "left", labelKey: "b0028" },
    { value: "right", labelKey: "b0029" }
];

const BannerButtonPositionInputGroup: React.FC<WebchatEmbeddedFormProps> = (props) => {
    const { embeddedSettings: propsEmbeddedSettings, legend, onChange } = props;
    
    const id = useId();
    const intl = useIntl();

    const [device, setDevice] = useState<"desktop" | "mobile">("desktop");
    const isDesktop = device === "desktop";

    const { setBannerMode } = useWebchatPreviewMode();

    const updateDeviceSetting = (changes: {[key: string]: any}) => {
        if (isDesktop) {
            onChange({
                ...propsEmbeddedSettings,
                ...changes,
            });
        } else {
            onChange({
                ...propsEmbeddedSettings,
                mobile: {
                    ...propsEmbeddedSettings.mobile!,
                    ...changes,
                }
            });
        }
    };

    const onChangeButtonSize = (value: number) => {
        // Calculate icon size based on button width on parent
        updateDeviceSetting({ buttonWidth: value });
    };

    const embeddedSetting = isDesktop ? propsEmbeddedSettings : propsEmbeddedSettings.mobile!;
    const isLeftPosition = embeddedSetting.buttonReferencePosition === "left";

    return (
        <fieldset className="flex flex-col gap-2">
            <legend className="sr-only">
                {legend}
            </legend>
            <div className="flex items-center gap-2">
                <button aria-pressed={isDesktop}
                        className={classNames(
                            "p-1.5 text-[14px]",
                            isDesktop ? "text-chat42 font-[600]" : "text-black-500 font-[300]"
                        )}
                        onClick={() => {
                            setDevice("desktop");
                            setBannerMode("desktop");
                        }}
                >
                    {intl.formatMessage({ id: "i09073" })}
                </button>

                <div className="w-px h-3.5 bg-black-300" />

                <button aria-pressed={!isDesktop}
                        className={classNames(
                            "p-1.5 text-[14px]",
                            !isDesktop ? "text-chat42 font-[600]" : "text-black-500 font-[300]"
                        )}
                        onClick={() => {
                            setDevice("mobile");
                            setBannerMode("mobile");
                        }}
                >
                    {intl.formatMessage({ id: "i09074" })}
                </button>
            </div>

            <div className="py-4 px-6 label-input-grid-[128] gap-x-2 gap-y-6 border border-black-200 rounded-[8px]">
                <div id="webchat-button-reference-position"
                     className="field-label"
                >
                    {intl.formatMessage({ id: "i09075" })}
                </div>
                <WebchatRadioGroup name="webchat-button-reference-position"
                                   aria-labelledby="webchat-button-reference-position"
                                   options={BUTTON_ALIGN_OPTIONS}
                                   selectedValue={embeddedSetting.buttonReferencePosition ?? "left"}
                                   onChange={v => updateDeviceSetting({ buttonReferencePosition: v })}
                />

                <div className="col-span-2 flex justify-between items-center">
                    <div className="w-[208px] flex justify-between items-center">
                        <label htmlFor="webchat-setting-btn-bottom" className="field-label">
                            {intl.formatMessage({ id: "i09076" })}
                        </label>
                        <NumberInput id="webchat-setting-btn-bottom"
                                     width={72}
                                     min={0}
                                     value={embeddedSetting.buttonBottom}
                                     onChange={e => updateDeviceSetting({ buttonBottom: parseInt(e.target.value) })}
                        />
                    </div>
                    <div className="w-[208px] pr-6 flex justify-between items-center">
                        <label htmlFor="webchat-setting-btn-side" className="field-label">
                            {isLeftPosition
                                ? intl.formatMessage({ id: "i09077" })
                                : intl.formatMessage({ id: "i09078" })}
                        </label>
                        <NumberInput id="webchat-setting-btn-side"
                                     width={72}
                                     min={0}
                                     value={isLeftPosition ? embeddedSetting.buttonLeft : embeddedSetting.buttonRight}
                                     onChange={e => updateDeviceSetting({
                                         [isLeftPosition ? "buttonLeft" : "buttonRight"]: parseInt(e.target.value)
                                     })}
                        />
                    </div>
                </div>

                <div id={id}
                     className="field-label">
                    {intl.formatMessage({ id: "i09079" })}
                </div>
                <RangeSlider width="100%"
                             useTooltip
                             min={28} max={60}
                             aria-labelledby={id}
                             aria-valuetext={v => `${v}px`}
                             value={embeddedSetting.buttonWidth}
                             onChange={onChangeButtonSize}
                />
            </div>
        </fieldset>
    );
};

export default BannerButtonPositionInputGroup;
